import { Link } from "react-router-dom";
import { Header } from "@/components/landing/Header";
import { Footer } from "@/components/landing/Footer";
import { SeoHead } from "@/components/seo/SeoHead";
import { StructuredData } from "@/components/seo/StructuredData";
import {
  getBaseUrl,
  buildBreadcrumbSchema,
  buildOrganizationSchema,
  buildSoftwareApplicationSchema,
  buildFAQPageSchema,
} from "@/lib/seo";
import { Button } from "@/components/ui/button";
import { Instagram, MessageCircle, ArrowRight } from "lucide-react";

const TITLE = "Automação de DM e comentários no Instagram com IA | FabChat";
const DESCRIPTION =
  "Automação de DM no Instagram com agente de IA: respostas em Direct e comentários, com contexto de conversa e integração ao CRM da FabChat.";

const BREADCRUMB = [
  { name: "Início", path: "/" },
  { name: "Automação de DM no Instagram", path: "/instagram/automacao-dm" },
];

const FAQ_ITEMS = [
  {
    question: "Como fazer automação no Direct do Instagram?",
    answer:
      "Na FabChat, a automação de Direct é feita por um agente de IA conectado ao seu perfil. Você define a personalidade, as regras e o que o agente deve responder; ele atende as DMs com contexto e encaminha para um humano quando faz sentido — o novo método para criar relacionamento com o cliente no Instagram em escala.",
  },
  {
    question: "É possível automatizar comentários no Instagram?",
    answer:
      "Sim. Além das DMs, o agente de IA acompanha comentários em posts e responde dentro das regras configuradas, ajudando a manter engajamento e relacionamento mesmo em contas com grande volume de interações.",
  },
  {
    question: "Mensagem automática no Instagram é a mesma coisa que agente de IA?",
    answer:
      "Não. Mensagem automática costuma ser uma resposta fixa para palavras-chave. O agente de IA da FabChat interpreta a intenção do seguidor, mantém o contexto e adapta a resposta às regras do negócio, criando uma experiência próxima a um atendimento humano.",
  },
  {
    question: "Posso unir Instagram e WhatsApp no mesmo painel?",
    answer:
      "Sim. As conversas de Instagram (DM e comentários) e WhatsApp ficam no mesmo painel da FabChat, com lead identificado e histórico unificado no CRM integrado. Assim o relacionamento continua quando o cliente troca de canal.",
  },
];

export default function InstagramAutomacaoDm() {
  const baseUrl = getBaseUrl();
  const breadcrumbSchema = buildBreadcrumbSchema(baseUrl, BREADCRUMB);
  const organizationSchema = buildOrganizationSchema(baseUrl);
  const softwareSchema = buildSoftwareApplicationSchema(baseUrl, DESCRIPTION);
  const faqSchema = buildFAQPageSchema(FAQ_ITEMS);

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SeoHead title={TITLE} description={DESCRIPTION} canonicalPath="/instagram/automacao-dm" />
      <StructuredData data={[organizationSchema, softwareSchema, breadcrumbSchema, faqSchema]} />
      <Header />

      <main className="flex-1">
        <section className="container mx-auto px-4 py-16 md:py-24 max-w-4xl">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Automação de DM no Instagram</h1>
          <p className="text-lg text-muted-foreground mb-12">
            Quem busca <strong className="text-foreground">automação de DM e comentários no Instagram</strong> encontra isso na FabChat: o <strong className="text-foreground">agente de IA</strong> da plataforma é quem faz as respostas em direct e a gestão de comentários — não é um produto à parte.
          </p>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold mb-4">Como funciona a automação de DM no Instagram</h2>
            <p className="text-muted-foreground mb-4">
              A automação de respostas em direct (DM) e em comentários é realizada pelo <strong className="text-foreground">agente de IA da FabChat</strong> conectado ao seu Instagram. Você configura comportamento, tom de voz e regras; o agente responde de forma humanizada e mantém o contexto. DMs e comentários são gerenciados no mesmo painel em que você atende o <Link to="/whatsapp/agente-ia" className="text-primary hover:underline">WhatsApp</Link>, com leads no <Link to="/integracoes/crm" className="text-primary hover:underline">CRM integrado</Link>.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold mb-4">O que a automação cobre no Instagram</h2>
            <ul className="space-y-3 text-muted-foreground">
              <li className="flex gap-3">
                <Instagram className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                <span>Respostas automáticas em direct (DM), com contexto mantido pelo agente de IA.</span>
              </li>
              <li className="flex gap-3">
                <MessageCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                <span>Gestão de comentários em posts: respostas e acompanhamento pela plataforma.</span>
              </li>
              <li className="flex gap-3">
                <MessageCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                <span>Chat unificado: Instagram e WhatsApp no mesmo lugar, com histórico e lead identificado.</span>
              </li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold mb-4">Para quem é indicado</h2>
            <p className="text-muted-foreground mb-4">
              Negócios que usam o Instagram para vendas ou atendimento e querem responder mais rápido às DMs e comentários. A automação é feita pelo agente de IA — personalidade e regras configuráveis, sem perder a identidade da marca. Fluxo único entre Instagram e WhatsApp quando o cliente trocar de canal.
            </p>
          </section>

          <p className="text-sm text-muted-foreground mb-6">
            A página do <strong className="text-foreground">agente de IA para Instagram</strong> explica em detalhe: personalidade, regras, contexto e como configurar o comportamento. É a página principal do produto no canal Instagram.
          </p>

          <section className="flex flex-col sm:flex-row gap-4 pt-6">
            <Button asChild size="lg" className="gap-2">
              <Link to="/instagram/agente-ia">
                Ver agente de IA para Instagram <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg">
              <Link to="/planos">Ver planos</Link>
            </Button>
            <Button asChild variant="ghost" size="lg">
              <Link to="/contato">Falar com a gente</Link>
            </Button>
          </section>
        </section>
      </main>

      <Footer />
    </div>
  );
}
